import { useQuery } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { regaValidateLicense, regaSendCompliance } from "@/services/api";
import { regaRepository } from "@/repositories";
import type {
  RegaValidateRequest,
  RegaValidateResponse,
  RegaComplianceRequest,
} from "@/types/rega";

export function useRegaValidate() {
  const { t } = useLanguage();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (data: RegaValidateRequest) => {
      const result = await regaValidateLicense(data);
      if (!result.success) throw new Error(result.message || "REGA validation failed");

      // REGA API returns nested: data.Body.result.advertisement
      // Navigate the envelope safely with runtime checks
      const raw = result.data;
      if (!raw || typeof raw !== "object") throw new Error("Empty REGA response");

      const body = ("Body" in raw ? raw.Body : raw) as Record<string, unknown> | undefined;
      const regaResult = (body && "result" in body ? body.result : body) as Record<string, unknown> | undefined;

      if (!regaResult) throw new Error("Invalid REGA response structure");

      if (regaResult.isValid === false) {
        throw new Error(String(regaResult.message || "License not valid"));
      }

      if (!regaResult.advertisement) throw new Error("No advertisement data in REGA response");

      return regaResult.advertisement as RegaValidateResponse;
    },
    onSuccess: (data) => {
      toast.success(t("تم التحقق من الرخصة بنجاح", "License validated successfully"));

      // NestJS validate endpoint doesn't receive companyId, so patch it after save
      if (companyId && data?.adLicenseNumber) {
        void regaRepository.patchLicenseCompanyId(data.adLicenseNumber, companyId);
      }
    },
    onError: (error) => {
      console.error("REGA validate error:", error);
      const msg = error.message || "";
      // Show specific bilingual messages based on common failure patterns
      if (msg.includes("REGA validate-license request failed") || msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        toast.error(t("تعذر الاتصال بخدمة هيئة العقار — يرجى المحاولة لاحقاً", "Could not connect to REGA service — please try again later"));
      } else if (msg.includes("License not valid") || msg.includes("isValid")) {
        toast.error(t("رقم الرخصة غير صالح أو غير متطابق مع رقم الهوية", "License number is invalid or does not match the ID number"));
      } else if (msg.includes("Empty REGA response") || msg.includes("Invalid REGA response")) {
        toast.error(t("استجابة غير متوقعة من هيئة العقار — يرجى المحاولة لاحقاً", "Unexpected response from REGA — please try again later"));
      } else {
        toast.error(msg || t("فشل التحقق من الرخصة", "License validation failed"));
      }
    },
  });
}

/**
 * Fire-and-forget REGA compliance mutation.
 * Shows warning toast on failure but never blocks the user flow.
 */
export function useRegaCompliance() {
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (data: RegaComplianceRequest) => {
      const result = await regaSendCompliance(data);
      if (!result.success) throw new Error(result.message || "Compliance call failed");
      return result.data;
    },
    onError: (error) => {
      console.error("REGA compliance error:", error);
      toast.warning(
        t(
          "تعذر إرسال إشعار الامتثال لهيئة العقار — سيتم إعادة المحاولة لاحقاً",
          "Failed to send REGA compliance notification — will retry later",
        ),
      );
    },
  });
}

/**
 * Query REGA compliance logs for a specific listing (or all for the company).
 */
export function useRegaComplianceLogs(listingId?: string) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["rega-compliance-logs", companyId, listingId],
    queryFn: async () => {
      return regaRepository.listComplianceLogs(companyId!, listingId);
    },
    enabled: !!companyId,
  });
}

/**
 * Query the REGA ad license linked to a specific listing.
 *
 * Joins on `ad_license_number` (the populated column) rather than `listing_id`
 * (which is NULL on all prod rows — the wizard's link step silently no-ops
 * because it targets an id that doesn't match). Pass the listing's own
 * `ad_license_number` field as the argument.
 *
 * NOT scoped by `company_id` here — deliberately. `rega_ad_licenses.company_id`
 * is nullable and is only patched onto a row after REGA validation completes
 * (see `useRegaValidate` above); adding `.eq("company_id", …)` would silently
 * hide legitimately-linked licenses whose company_id hasn't been backfilled
 * yet, regressing the existing `AdRegaActivity` panel. Tenancy is enforced by
 * the `rega_licenses_select_admin` RLS policy (company-scoped, admin-only —
 * see the note in `src/repositories/listings.ts`). Tracked for a company_id-safe
 * hardening pass in a follow-up: https://github.com/Dallali-App/dallali.com/issues/3065
 */
export function useRegaLicenseForListing(adLicenseNumber?: string | null) {
  return useQuery({
    queryKey: ["rega-license", adLicenseNumber],
    queryFn: async () => {
      if (!adLicenseNumber) return null;
      return regaRepository.getLicenseByAdNumber(adLicenseNumber);
    },
    enabled: !!adLicenseNumber,
  });
}

/**
 * Query all REGA ad licenses for the company.
 */
export function useRegaLicenses() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["rega-licenses", companyId],
    queryFn: async () => {
      return regaRepository.listLicenses(companyId!);
    },
    enabled: !!companyId,
  });
}

/**
 * Query REGA inbound notifications.
 */
export function useRegaNotifications() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["rega-notifications", companyId],
    queryFn: async () => {
      return regaRepository.listNotifications(companyId!);
    },
    enabled: !!companyId,
  });
}
